import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { apiFetch } from '../../api/client';
import { MAX_ATTACH_BYTES, fileToBase64, formatBytes, quillHtmlToInline, stripHtml } from './emailUtils';

const FONT_VALUES = ['arial', 'times-new-roman', 'georgia', 'calibri', 'verdana', 'tahoma', 'courier-new'];

function ensureEmailQuillFonts() {
  const Quill = ReactQuill.Quill;
  if (!Quill || Quill.__emailFonts) return;
  try {
    const Font = Quill.import('formats/font');
    Font.whitelist = FONT_VALUES;
    Quill.register(Font, true);
    Quill.__emailFonts = true;
  } catch {
    /* toolbar ainda funciona sem a lista customizada */
  }
}
ensureEmailQuillFonts();

const MODULES = {
  toolbar: [
    [{ font: FONT_VALUES }, { size: ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    ['link', 'image', 'clean'],
  ],
};

const FORMATS = [
  'font', 'size', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'align', 'list', 'bullet', 'indent', 'link', 'image',
];

const TONES = [
  { id: 'formal', label: 'Mais formal' },
  { id: 'informal', label: 'Mais informal' },
  { id: 'direto', label: 'Mais direto' },
];

export default function EmailComposer({ open, mode, initial, replyId, originalText, onClose, onSent }) {
  const [to, setTo] = useState(initial?.to || '');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState(initial?.subject || '');
  const [html, setHtml] = useState(initial?.html || '');
  const [lineHeight, setLineHeight] = useState('1.5');
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiAction, setAiAction] = useState('generate');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiTone, setAiTone] = useState('formal');
  const [aiPreview, setAiPreview] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const inputRef = useRef(null);

  const totalBytes = files.reduce((sum, f) => sum + (f.file?.size || 0), 0);

  const addFiles = (list) => {
    const next = Array.from(list || []);
    if (!next.length) return;
    const merged = [...files];
    let bytes = totalBytes;
    for (const file of next) {
      bytes += file.size;
      if (bytes > MAX_ATTACH_BYTES) {
        setError('Os anexos passam de 25 MB (limite do Gmail). Remova algum arquivo.');
        return;
      }
      merged.push({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
        file,
      });
    }
    setError('');
    setFiles(merged);
  };

  const runAi = async () => {
    setAiLoading(true);
    setError('');
    try {
      const res = await apiFetch('/api/gmail/ai/compose', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: aiAction,
          prompt: aiPrompt,
          text: stripHtml(html),
          tone: aiAction === 'tone' ? aiTone : undefined,
          originalEmail: aiAction === 'reply' ? originalText : undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'A IA não respondeu.');
      setAiPreview(data.text || '');
    } catch (e) {
      setError(e.message || 'Falha na IA');
    } finally {
      setAiLoading(false);
    }
  };

  const applyAi = () => {
    const asHtml = aiPreview.includes('<') ? aiPreview : `<p>${aiPreview.replace(/\n/g, '<br/>')}</p>`;
    setHtml(asHtml);
    setAiPreview('');
    setAiOpen(false);
  };

  const send = async () => {
    if (!to.trim() || !subject.trim()) {
      setError('Informe destinatário e assunto.');
      return;
    }
    if (totalBytes > MAX_ATTACH_BYTES) {
      setError('Os anexos passam de 25 MB (limite do Gmail).');
      return;
    }
    setSending(true);
    setError('');
    try {
      const attachments = [];
      for (const item of files) {
        attachments.push({
          filename: item.file.name,
          mimeType: item.file.type || 'application/octet-stream',
          contentBase64: await fileToBase64(item.file),
        });
      }
      const bodyHtml = `<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:${lineHeight};color:#1a1a1a">${quillHtmlToInline(html)}</div>`;
      const payload = {
        to: to.trim(),
        cc: cc.trim() || undefined,
        subject: subject.trim(),
        html: bodyHtml,
        text: stripHtml(html),
        attachments,
      };
      const url = mode === 'reply' && replyId
        ? `/api/gmail/messages/${encodeURIComponent(replyId)}/reply`
        : '/api/gmail/messages/send';
      const res = await apiFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Falha no envio');
      onSent?.();
    } catch (e) {
      setError(e.message || 'Falha no envio');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {mode === 'reply' ? 'Responder' : 'Novo e-mail'}
        <IconButton onClick={onClose} disabled={sending} aria-label="Fechar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.25} sx={{ mt: 0.5 }}>
          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
          <TextField label="Para" size="small" fullWidth value={to} onChange={(e) => setTo(e.target.value)} />
          <TextField label="Cc" size="small" fullWidth value={cc} onChange={(e) => setCc(e.target.value)} />
          <TextField label="Assunto" size="small" fullWidth value={subject} onChange={(e) => setSubject(e.target.value)} />
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              select
              size="small"
              label="Espaçamento"
              value={lineHeight}
              onChange={(e) => setLineHeight(e.target.value)}
              sx={{ width: 160 }}
            >
              <MenuItem value="1.15">Compacto</MenuItem>
              <MenuItem value="1.5">Normal</MenuItem>
              <MenuItem value="1.8">Folgado</MenuItem>
              <MenuItem value="2">Duplo</MenuItem>
            </TextField>
            <Tooltip title="IA: gerar, corrigir ou sugerir resposta">
              <Button startIcon={<AutoAwesomeIcon />} onClick={() => { setAiOpen(true); setAiPreview(''); }}>
                IA
              </Button>
            </Tooltip>
            <Button startIcon={<AttachFileIcon />} onClick={() => inputRef.current?.click()}>
              Anexar
            </Button>
            <input
              ref={inputRef}
              type="file"
              hidden
              multiple
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </Stack>

          <Box
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              addFiles(e.dataTransfer.files);
            }}
            sx={{
              borderRadius: 2,
              border: '1px dashed',
              borderColor: dragOver ? 'primary.main' : 'divider',
              bgcolor: dragOver ? 'rgba(25,118,210,0.06)' : '#fff',
              '& .ql-toolbar.ql-snow': { border: 'none', borderBottom: '1px solid rgba(15,23,42,0.08)', flexWrap: 'wrap' },
              '& .ql-container.ql-snow': { border: 'none', minHeight: 180 },
              '& .ql-editor': { minHeight: 180, lineHeight },
            }}
          >
            <ReactQuill theme="snow" value={html} onChange={setHtml} modules={MODULES} formats={FORMATS} placeholder="Escreva a mensagem…" />
          </Box>

          {files.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {files.map((item) => (
                <Chip
                  key={item.id}
                  label={`${item.file.name} · ${formatBytes(item.file.size)}`}
                  onDelete={() => setFiles((prev) => prev.filter((f) => f.id !== item.id))}
                  sx={{ maxWidth: 280 }}
                />
              ))}
              <Typography variant="caption" color={totalBytes > MAX_ATTACH_BYTES ? 'error' : 'text.secondary'} sx={{ alignSelf: 'center' }}>
                {formatBytes(totalBytes)} / 25 MB
              </Typography>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>Cancelar</Button>
        <Button variant="contained" startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />} disabled={sending} onClick={send}>
          Enviar
        </Button>
      </DialogActions>

      <Dialog open={aiOpen} onClose={() => !aiLoading && setAiOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Assistente de texto</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <TextField select size="small" label="Ação" value={aiAction} onChange={(e) => setAiAction(e.target.value)}>
              <MenuItem value="generate">Gerar e-mail do zero</MenuItem>
              <MenuItem value="correct">Corrigir texto</MenuItem>
              <MenuItem value="tone">Melhorar tom / clareza</MenuItem>
              <MenuItem value="reply">Resumir e sugerir resposta</MenuItem>
            </TextField>
            {aiAction === 'tone' && (
              <TextField select size="small" label="Tom" value={aiTone} onChange={(e) => setAiTone(e.target.value)}>
                {TONES.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>
                ))}
              </TextField>
            )}
            {(aiAction === 'generate' || aiAction === 'reply') && (
              <TextField
                label={aiAction === 'generate' ? 'O que você quer dizer?' : 'Instrução extra (opcional)'}
                fullWidth
                multiline
                minRows={2}
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
              />
            )}
            <Button variant="outlined" onClick={runAi} disabled={aiLoading}>
              {aiLoading ? 'Gerando…' : aiPreview ? 'Tentar novamente' : 'Gerar prévia'}
            </Button>
            {aiPreview && (
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(15,23,42,0.04)', whiteSpace: 'pre-wrap' }}>
                <Typography variant="caption" color="text.secondary">Prévia — o texto atual não será substituído até você confirmar.</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>{aiPreview}</Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiOpen(false)}>Cancelar</Button>
          <Button variant="contained" disabled={!aiPreview} onClick={applyAi}>Usar este texto</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
